import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiClient } from "./ApiProvider";
import type {
  ChangePasswordInput,
  CreateInstanceInput,
  CreateProjectInput,
  CreateProviderCredentialInput,
  CreateServiceInput,
  CreateUserInput,
  DeployStackInput,
  EnrollToken,
  Page,
  PageParams,
  RotateProviderCredentialInput,
  UpdateProjectInput,
  UpdateServiceInput,
  UpdateUserInput,
} from "./client";
import type { ApiError } from "./errors";
import type {
  AuditVerification,
  Container,
  ContainerEvent,
  Instance,
  Project,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  Stack,
  User,
} from "./types";

/**
 * Central query-key factory so every hook and cache write agrees on shape.
 *
 * Every list key carries an explicit `"list"` segment and every single-item
 * key an explicit `"detail"`/id segment, kept distinct on purpose: list
 * hooks below append `[cursor, limit]` onto a *list* key per page (FE-H2),
 * and several cache writes use `setQueriesData`/`invalidateQueries` with a
 * *prefix* match against these keys — without the "list" vs "detail" split,
 * a prefix match against `containers(fleetId)` would also sweep up
 * `container(fleetId, nodeId, containerId)` detail entries (same leading
 * segments) and apply a list-shaped (`Page<T>`) updater to a bare `T`.
 */
export const queryKeys = {
  fleets: () => ["fleets"] as const,
  containers: (fleetId: string) => ["containers", "list", fleetId] as const,
  // A container id is only unique per node, so its cache key includes nodeId too
  // (mirrors the real backend's routing — see client.ts).
  container: (fleetId: string, nodeId: string, containerId: string) =>
    ["containers", "detail", fleetId, nodeId, containerId] as const,
  stacks: (fleetId: string) => ["stacks", "list", fleetId] as const,
  stack: (fleetId: string, id: string) => ["stacks", "detail", fleetId, id] as const,
  nodes: (fleetId: string) => ["nodes", "list", fleetId] as const,
  secrets: (fleetId: string) => ["secrets", "list", fleetId] as const,
  // ---- Admin/user portal ----
  users: () => ["users", "list"] as const,
  account: () => ["account"] as const,
  audit: (fleetId: string) => ["audit", "list", fleetId] as const,
  // ---- Hero flow ----
  projects: (fleetId: string) => ["projects", "list", fleetId] as const,
  project: (fleetId: string, id: string) => ["projects", "detail", fleetId, id] as const,
  services: (fleetId: string, projectId: string) => ["services", "list", fleetId, projectId] as const,
  service: (fleetId: string, projectId: string, id: string) =>
    ["services", "detail", fleetId, projectId, id] as const,
  deployments: (fleetId: string, projectId: string, serviceId: string) =>
    ["deployments", "list", fleetId, projectId, serviceId] as const,
  providerCredentials: (fleetId: string) => ["providerCredentials", "list", fleetId] as const,
  instances: (fleetId: string, projectId?: string) =>
    ["instances", "list", fleetId, projectId ?? null] as const,
  instance: (fleetId: string, id: string) => ["instances", "detail", fleetId, id] as const,
  // Billing is environment-wide like users/account: the server scopes it to the
  // session's tenant, so there is no fleet id in the key.
  subscription: () => ["billing", "subscription"] as const,
  entitlement: () => ["billing", "entitlement"] as const,
};

/** Appends the page params any paginated list's query key includes, so
 * every cursor/limit combination gets its own cache entry (FE-H2) while
 * `queryKeys.<thing>(fleetId)` alone stays a stable prefix for
 * invalidate/patch across every page. */
function pageKeySuffix(params: PageParams): readonly [string | null, number | null] {
  return [params.cursor ?? null, params.limit ?? null];
}

/** How often to poll while a stack is mid-deploy (P-1 seam, no event stream
 * for stacks yet — same gap as containers, see subscribeToContainerEvents).
 * Idle otherwise: a redeploy/deploy/down already invalidates its query on
 * success for an immediate refresh. */
const STACK_DEPLOYING_POLL_MS = 2000;

/** `enabled: false` is how a role with no fleet grants (billing_admin) avoids
 * firing a request that could only answer 403 — a coherence choice, not a
 * control (the server decides, S-8). */
export function useFleets(options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.fleets(),
    queryFn: () => client.listFleets(),
    enabled: options?.enabled ?? true,
  });
}

export function useContainers(fleetId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.containers(fleetId), ...pageKeySuffix(params)],
    queryFn: () => client.listContainers({ fleetId, ...params }),
    enabled: Boolean(fleetId),
    // The real control plane has no event stream yet (P-1 seam deferred — see
    // realClient.ts's subscribeToContainerEvents). A start/stop/restart already
    // invalidates this query for an immediate refresh (below); this modest
    // background refetch is the one deliberate exception to "event-driven, not
    // polled" so the list still catches changes made from outside this UI (e.g.
    // `docker` on the host). Do not add polling anywhere else.
    refetchInterval: 20_000,
    // Keeps the current page's rows on screen while the next page loads
    // rather than flashing to a loading state on every Previous/Next (FE-H2).
    placeholderData: keepPreviousData,
  });
}

export function useContainer(fleetId: string, nodeId: string | undefined, containerId: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.container(fleetId, nodeId ?? "", containerId ?? ""),
    queryFn: () => client.getContainer(fleetId, nodeId as string, containerId as string),
    enabled: Boolean(fleetId) && Boolean(nodeId) && Boolean(containerId),
  });
}

export function useStacks(fleetId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.stacks(fleetId), ...pageKeySuffix(params)],
    queryFn: () => client.listStacks({ fleetId, ...params }),
    enabled: Boolean(fleetId),
    // No event stream for stacks yet (P-1 seam, same gap as containers) — a
    // deploy/redeploy/down already invalidates this list on success (below),
    // so this only needs to catch a deploy still in flight (poll faster
    // while any row is "deploying") or a change made from outside this UI.
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((s) => s.status === "deploying") ? STACK_DEPLOYING_POLL_MS : 20_000;
    },
    placeholderData: keepPreviousData,
  });
}

export function useStack(fleetId: string, id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.stack(fleetId, id ?? ""),
    queryFn: () => client.getStack(fleetId, id as string),
    enabled: Boolean(fleetId) && Boolean(id),
    refetchInterval: (query) => (query.state.data?.status === "deploying" ? STACK_DEPLOYING_POLL_MS : false),
  });
}

export function useNodes(fleetId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.nodes(fleetId), ...pageKeySuffix(params)],
    queryFn: () => client.listNodes({ fleetId, ...params }),
    enabled: Boolean(fleetId),
    placeholderData: keepPreviousData,
  });
}

export function useSecrets(fleetId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.secrets(fleetId), ...pageKeySuffix(params)],
    queryFn: () => client.listSecrets({ fleetId, ...params }),
    enabled: Boolean(fleetId),
    placeholderData: keepPreviousData,
  });
}

/**
 * Patches every cached page of the containers list (any cursor — FE-H2 means
 * more than one page can be cached at once) plus the single detail entry, in
 * place. Uses `setQueriesData`'s prefix match against `queryKeys.containers`
 * rather than a single `setQueryData` at one fixed key, since which page(s)
 * are cached (and which one holds this container) varies with how far the
 * operator has paged. Safe from cross-contaminating the detail cache (a bare
 * `Container`, not a `Page<Container>`) because `container()`'s key carries
 * a `"detail"` segment `containers()`'s doesn't share — see queryKeys above.
 */
function patchContainerCaches(
  queryClient: QueryClient,
  fleetId: string,
  nodeId: string,
  containerId: string,
  patch: Partial<Container>,
) {
  queryClient.setQueryData<Container>(queryKeys.container(fleetId, nodeId, containerId), (current) =>
    current ? { ...current, ...patch } : current,
  );
  queryClient.setQueriesData<Page<Container>>({ queryKey: queryKeys.containers(fleetId) }, (current) => {
    if (!current) return current;
    return {
      ...current,
      items: current.items.map((c) => (c.id === containerId && c.nodeId === nodeId ? { ...c, ...patch } : c)),
    };
  });
}

interface ActionVariables {
  nodeId: string;
  containerId: string;
}

interface ActionContext {
  previousDetail?: Container;
  /** Every cached containers-list page as of `onMutate`, snapshotted by
   * exact key so `onError` can restore each one precisely — see
   * `patchContainerCaches`. */
  previousLists: Array<[QueryKey, Page<Container> | undefined]>;
  nodeId: string;
  containerId: string;
}

type ContainerActionKind = "start" | "stop" | "restart";

const OPTIMISTIC_COPY: Record<ContainerActionKind, { state: Container["state"]; status: string }> = {
  start: { state: "running", status: "Starting…" },
  stop: { state: "exited", status: "Stopping…" },
  restart: { state: "restarting", status: "Restarting…" },
};

function useContainerAction(fleetId: string, kind: ContainerActionKind) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const action =
    kind === "start" ? client.startContainer : kind === "stop" ? client.stopContainer : client.restartContainer;

  return useMutation<Container, ApiError, ActionVariables, ActionContext>({
    mutationFn: ({ nodeId, containerId }) => action(fleetId, nodeId, containerId),
    onMutate: async ({ nodeId, containerId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.container(fleetId, nodeId, containerId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.containers(fleetId) });
      const previousDetail = queryClient.getQueryData<Container>(queryKeys.container(fleetId, nodeId, containerId));
      const previousLists = queryClient.getQueriesData<Page<Container>>({ queryKey: queryKeys.containers(fleetId) });
      patchContainerCaches(queryClient, fleetId, nodeId, containerId, OPTIMISTIC_COPY[kind]);
      return { previousDetail, previousLists, nodeId, containerId };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      if (context.previousDetail) {
        queryClient.setQueryData(
          queryKeys.container(fleetId, context.nodeId, context.containerId),
          context.previousDetail,
        );
      }
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (updated, { nodeId, containerId }) => {
      patchContainerCaches(queryClient, fleetId, nodeId, containerId, updated);
      // No real event stream yet (P-1 seam deferred — see realClient.ts): the
      // direct cache write above already reflects this action's own result
      // immediately, and invalidating the list on top catches anything else
      // that changed underneath it (another operator, `docker` on the host,
      // etc.) with one extra fetch rather than a standing poll.
      void queryClient.invalidateQueries({ queryKey: queryKeys.containers(fleetId) });
    },
  });
}

export function useStartContainer(fleetId: string) {
  return useContainerAction(fleetId, "start");
}

export function useStopContainer(fleetId: string) {
  return useContainerAction(fleetId, "stop");
}

export function useRestartContainer(fleetId: string) {
  return useContainerAction(fleetId, "restart");
}

/**
 * The P-1 seam made concrete: subscribes to the (mock: simulated; real: not yet
 * implemented — see realClient.ts) event stream for a fleet and patches the
 * query cache directly rather than refetching. Mount once per active fleet (see
 * `AppShell`) and every list/detail view fed by `queryKeys.containers`/
 * `queryKeys.container` updates live with no polling involved once the real
 * event channel lands.
 */
export function useContainerEventsSubscription(fleetId: string): void {
  const client = useApiClient();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!fleetId) return;
    const unsubscribe = client.subscribeToContainerEvents(fleetId, (event: ContainerEvent) => {
      patchContainerCaches(queryClient, fleetId, event.nodeId, event.containerId, {
        state: event.state,
        status: event.status,
      });
    });
    return unsubscribe;
  }, [client, fleetId, queryClient]);
}

// ---- Secrets (write-only — see api/types.ts) ----
// Every mutation below invalidates the list on success rather than writing an
// optimistic patch: there is no event stream for secrets (P-1 seam, same gap
// as containers today) and, unlike a container action, there is no value to
// echo back into the cache anyway — only refreshed metadata.

export function useCreateSecret(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SecretSummary, ApiError, { name: string; value: string }>({
    mutationFn: ({ name, value }) => client.createSecret(fleetId, { name, value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.secrets(fleetId) });
    },
  });
}

export function useRotateSecret(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SecretSummary, ApiError, { secretId: string; value: string }>({
    mutationFn: ({ secretId, value }) => client.rotateSecret(fleetId, secretId, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.secrets(fleetId) });
    },
  });
}

export function useDeleteSecret(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { secretId: string }>({
    mutationFn: ({ secretId }) => client.deleteSecret(fleetId, secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.secrets(fleetId) });
    },
  });
}

// ---- Stacks (C-9): file-native compose deploys ----
// Every mutation writes the returned Stack straight into the detail cache (it
// already has everything a refetch would return) and invalidates the list —
// same "direct write + invalidate, no standing poll" shape as the container
// actions above, since there's no event stream here either (P-1 seam).

export function useDeployStack(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Stack, ApiError, DeployStackInput>({
    mutationFn: (input) => client.deployStack(fleetId, input),
    onSuccess: (stack) => {
      queryClient.setQueryData(queryKeys.stack(fleetId, stack.id), stack);
      void queryClient.invalidateQueries({ queryKey: queryKeys.stacks(fleetId) });
    },
  });
}

export function useRedeployStack(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Stack, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.redeployStack(fleetId, id),
    onSuccess: (stack) => {
      queryClient.setQueryData(queryKeys.stack(fleetId, stack.id), stack);
      void queryClient.invalidateQueries({ queryKey: queryKeys.stacks(fleetId) });
    },
    // Deliberately no onError cache write: a refused redeploy (403) leaves the
    // stack running exactly as it was (charter §1 safety corollary) — the
    // existing cached Stack is still accurate, nothing to reconcile.
  });
}

export function useDeleteStack(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteStack(fleetId, id),
    onSuccess: (_data, { id }) => {
      queryClient.removeQueries({ queryKey: queryKeys.stack(fleetId, id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stacks(fleetId) });
    },
  });
}

// ---- Enrollment (C-6: "add a host") ----
// Deliberately no query-cache involvement: a join token is single-use,
// short-lived, shown once, and never persisted (S-13) — there is nothing here
// that belongs in a cache another view could read back.

export function useCreateEnrollToken(fleetId: string) {
  const client = useApiClient();
  return useMutation<EnrollToken, ApiError, void>({
    mutationFn: () => client.createEnrollToken(fleetId),
  });
}

// ---- Admin: users (admin/user portal) ----
// Every mutation invalidates the list on success — same "no event stream yet,
// invalidate and refetch" shape as secrets/stacks above. `enabled` is threaded
// through so a page can call this hook unconditionally (rules of hooks) while
// still skipping the request entirely for a non-admin viewer (see UsersPage).

export function useUsers(params: PageParams = {}, options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.users(), ...pageKeySuffix(params)],
    queryFn: () => client.listUsers(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, CreateUserInput>({
    mutationFn: (input) => client.createUser(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

export function useUpdateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, { id: string; input: UpdateUserInput }>({
    mutationFn: ({ id, input }) => client.updateUser(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

export function useResetUserPassword() {
  const client = useApiClient();
  return useMutation<void, ApiError, { id: string; password: string }>({
    mutationFn: ({ id, password }) => client.resetUserPassword(id, password),
  });
}

export function useDeleteUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteUser(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}

// ---- Account (the caller's own profile/credential) ----

export function useAccount() {
  const client = useApiClient();
  return useQuery({ queryKey: queryKeys.account(), queryFn: () => client.getAccount() });
}

export function useChangePassword() {
  const client = useApiClient();
  return useMutation<void, ApiError, ChangePasswordInput>({
    mutationFn: (input) => client.changePassword(input),
  });
}

// ---- Audit log (C-13) ----
// `placeholderData: keepPreviousData` keeps the current page's rows on screen
// while the next page loads instead of flashing to a loading state on every
// Next/Previous click — the list is still small and bounded (one page, P-6),
// this is purely a continuity nicety.

export function useAuditEvents(fleetId: string, params: PageParams, options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.audit(fleetId), ...pageKeySuffix(params)],
    queryFn: () => client.listAuditEvents({ fleetId, ...params }),
    enabled: Boolean(fleetId) && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
  });
}

export function useVerifyAuditChain(fleetId: string) {
  const client = useApiClient();
  return useMutation<AuditVerification, ApiError, void>({
    mutationFn: () => client.verifyAuditChain(fleetId),
  });
}

// ---- Hero flow: project -> service -> deploy (+ Hetzner provisioning) ----
// Mirrors the real, merged backend contract (#63 projectsvc, #62
// provisioning) exactly — see the header notes in api/types.ts/client.ts.

export function useProjects(fleetId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.projects(fleetId), ...pageKeySuffix(params)],
    queryFn: () => client.listProjects({ fleetId, ...params }),
    enabled: Boolean(fleetId),
    placeholderData: keepPreviousData,
  });
}

export function useProject(fleetId: string, id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.project(fleetId, id ?? ""),
    queryFn: () => client.getProject(fleetId, id as string),
    enabled: Boolean(fleetId) && Boolean(id),
  });
}

export function useCreateProject(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Project, ApiError, CreateProjectInput>({
    mutationFn: (input) => client.createProject(fleetId, input),
    onSuccess: (project) => {
      queryClient.setQueryData(queryKeys.project(fleetId, project.id), project);
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(fleetId) });
    },
  });
}

export function useUpdateProject(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Project, ApiError, { id: string; input: UpdateProjectInput }>({
    mutationFn: ({ id, input }) => client.updateProject(fleetId, id, input),
    onSuccess: (project) => {
      queryClient.setQueryData(queryKeys.project(fleetId, project.id), project);
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(fleetId) });
    },
  });
}

export function useDeleteProject(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteProject(fleetId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(fleetId) });
    },
  });
}

export function useServices(fleetId: string, projectId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.services(fleetId, projectId), ...pageKeySuffix(params)],
    queryFn: () => client.listServices({ fleetId, projectId, ...params }),
    enabled: Boolean(fleetId) && Boolean(projectId),
    // No event stream for services yet (P-1 seam, same gap as stacks/
    // containers) — poll faster while any row is mid-deploy so the list
    // catches the deploying -> running/error transition promptly.
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((s) => s.status === "deploying") ? STACK_DEPLOYING_POLL_MS : 20_000;
    },
    placeholderData: keepPreviousData,
  });
}

export function useService(fleetId: string, projectId: string, id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.service(fleetId, projectId, id ?? ""),
    queryFn: () => client.getService(fleetId, projectId, id as string),
    enabled: Boolean(fleetId) && Boolean(projectId) && Boolean(id),
    refetchInterval: (query) => (query.state.data?.status === "deploying" ? STACK_DEPLOYING_POLL_MS : false),
  });
}

export function useCreateService(fleetId: string, projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Service, ApiError, CreateServiceInput>({
    mutationFn: (input) => client.createService(fleetId, projectId, input),
    onSuccess: (service) => {
      queryClient.setQueryData(queryKeys.service(fleetId, projectId, service.id), service);
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(fleetId, projectId) });
    },
  });
}

export function useUpdateService(fleetId: string, projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Service, ApiError, { id: string; input: UpdateServiceInput }>({
    mutationFn: ({ id, input }) => client.updateService(fleetId, projectId, id, input),
    onSuccess: (service) => {
      queryClient.setQueryData(queryKeys.service(fleetId, projectId, service.id), service);
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(fleetId, projectId) });
    },
  });
}

export function useDeleteService(fleetId: string, projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteService(fleetId, projectId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.services(fleetId, projectId) });
    },
  });
}

/**
 * NOT IMPLEMENTED server-side (see `ApiClient.deployService`'s doc comment)
 * — this mutation always rejects with a 501 `ApiError`. It exists so the UI
 * can offer the action and render the server's own honest refusal (Q-7)
 * rather than hide a capability an operator will reasonably look for;
 * `onSuccess` is deliberately absent — there is no cache to patch for a call
 * that cannot succeed.
 */
export function useDeployService(fleetId: string, projectId: string) {
  const client = useApiClient();
  return useMutation<never, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deployService(fleetId, projectId, id),
  });
}

export function useDeployments(
  fleetId: string,
  projectId: string,
  serviceId: string | undefined,
  params: PageParams = {},
) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.deployments(fleetId, projectId, serviceId ?? ""), ...pageKeySuffix(params)],
    queryFn: () => client.listDeployments({ fleetId, projectId, serviceId: serviceId as string, ...params }),
    enabled: Boolean(fleetId) && Boolean(projectId) && Boolean(serviceId),
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((d) => d.status === "pending" || d.status === "deploying") ? STACK_DEPLOYING_POLL_MS : false;
    },
    placeholderData: keepPreviousData,
  });
}

// ---- Hero flow: provider credentials (write-only — S-13) ----

export function useProviderCredentials(fleetId: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.providerCredentials(fleetId), ...pageKeySuffix(params)],
    queryFn: () => client.listProviderCredentials({ fleetId, ...params }),
    enabled: Boolean(fleetId),
    placeholderData: keepPreviousData,
  });
}

export function useCreateProviderCredential(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<ProviderCredentialSummary, ApiError, CreateProviderCredentialInput>({
    mutationFn: (input) => client.createProviderCredential(fleetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providerCredentials(fleetId) });
    },
  });
}

export function useRotateProviderCredential(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<ProviderCredentialSummary, ApiError, { id: string; input: RotateProviderCredentialInput }>({
    mutationFn: ({ id, input }) => client.rotateProviderCredential(fleetId, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providerCredentials(fleetId) });
    },
  });
}

export function useDeleteProviderCredential(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteProviderCredential(fleetId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.providerCredentials(fleetId) });
    },
  });
}

// ---- Hero flow: instances (Hetzner provisioning) ----

export function useInstances(fleetId: string, projectId?: string, params: PageParams = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...queryKeys.instances(fleetId, projectId), ...pageKeySuffix(params)],
    queryFn: () => client.listInstances({ fleetId, projectId, ...params }),
    enabled: Boolean(fleetId),
    // No event stream for instances yet (P-1 seam) — poll while any row is
    // still non-terminal, same shape as useServices above.
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      return items?.some((i) => i.status === "requested" || i.status === "provisioning" || i.status === "deleting")
        ? STACK_DEPLOYING_POLL_MS
        : 20_000;
    },
    placeholderData: keepPreviousData,
  });
}

export function useInstance(fleetId: string, id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.instance(fleetId, id ?? ""),
    queryFn: () => client.getInstance(fleetId, id as string),
    enabled: Boolean(fleetId) && Boolean(id),
  });
}

export function useCreateInstance(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Instance, ApiError, CreateInstanceInput>({
    mutationFn: (input) => client.createInstance(fleetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.instances(fleetId) });
    },
  });
}

export function useDeleteInstance(fleetId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) => client.deleteInstance(fleetId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.instances(fleetId) });
    },
  });
}

// ---- Billing (L-1) ----
// Reachable by admin and billing_admin; the server enforces that on every
// call, these hooks just surface whatever it answers (a 403 arrives as the
// same ApiError shape as anywhere else).

export function useSubscription(options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.subscription(),
    queryFn: () => client.getSubscription(),
    enabled: options?.enabled ?? true,
  });
}

/** Starts a checkout and resolves to the Stripe-hosted URL. The caller
 * navigates to it; it is a short-lived bearer capability, so it is never
 * stored, logged, or put in app state beyond the navigation itself (S-13). */
export function useStartCheckout() {
  const client = useApiClient();
  return useMutation<string, ApiError, { email?: string } | void>({
    mutationFn: (input) => client.startCheckout(input?.email),
  });
}

export function useOpenBillingPortal() {
  const client = useApiClient();
  return useMutation<string, ApiError, void>({
    mutationFn: () => client.openBillingPortal(),
  });
}

// ---- Licensing / entitlement (ADR-0017) ----
// `GET /api/entitlement` is `billing:view`, same reach as the subscription
// read above (admin + billing_admin) — callers MUST pass `enabled: false`
// for any other role (see EntitlementBanner/LicenseSection) so an
// operator/viewer never fires a request that can only answer 403.

export function useEntitlement(options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.entitlement(),
    queryFn: () => client.getEntitlement(),
    enabled: options?.enabled ?? true,
  });
}
